"use client";

/* eslint-disable @next/next/no-img-element -- the Figma arrow is an exact local SVG asset. */

import { type CSSProperties, useCallback, useEffect, useState } from "react";

type ScrollableRailControlsProps = {
  controlsNodeId?: string;
  controlsClassName?: string;
  indicatorNodeId?: string;
  indicatorClassName?: string;
  indicatorThumbNodeId?: string;
  indicatorThumbClassName?: string;
  itemCount: number;
  hideWhenNotScrollable?: boolean;
  nextNodeId?: string;
  previousNodeId?: string;
  showButtons?: boolean;
  showIndicator?: boolean;
  targetId?: string;
  targetSelector?: string;
  testIdPrefix: string;
};

type RailState = {
  canScroll: boolean;
  canGoNext: boolean;
  canGoPrevious: boolean;
  progress: number;
};

const initialState: RailState = {
  canScroll: false,
  canGoNext: false,
  canGoPrevious: false,
  progress: 0
};

export default function ScrollableRailControls({
  controlsNodeId,
  controlsClassName,
  indicatorNodeId,
  indicatorClassName,
  indicatorThumbNodeId,
  indicatorThumbClassName,
  itemCount,
  hideWhenNotScrollable = false,
  nextNodeId,
  previousNodeId,
  showButtons = true,
  showIndicator = false,
  targetId,
  targetSelector,
  testIdPrefix
}: ScrollableRailControlsProps) {
  const [railState, setRailState] = useState(initialState);

  const getRail = useCallback(() => {
    if (targetId) return document.getElementById(targetId);
    return targetSelector ? document.querySelector<HTMLElement>(targetSelector) : null;
  }, [targetId, targetSelector]);

  const updateRailState = useCallback(() => {
    const rail = getRail();
    if (!rail) return;

    const maximumScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const nextState = {
      canScroll: maximumScroll > 1,
      canGoNext: maximumScroll > 1 && rail.scrollLeft < maximumScroll - 1,
      canGoPrevious: rail.scrollLeft > 1,
      progress: maximumScroll > 0 ? Math.min(1, Math.max(0, rail.scrollLeft / maximumScroll)) : 0
    };
    setRailState(nextState);
  }, [getRail]);

  useEffect(() => {
    const rail = getRail();
    if (!rail) return;

    const resizeObserver = typeof ResizeObserver === "function"
      ? new ResizeObserver(updateRailState)
      : null;
    resizeObserver?.observe(rail);
    rail.addEventListener("scroll", updateRailState, { passive: true });
    window.addEventListener("resize", updateRailState, { passive: true });
    const frame = window.requestAnimationFrame(updateRailState);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      rail.removeEventListener("scroll", updateRailState);
      window.removeEventListener("resize", updateRailState);
    };
  }, [getRail, itemCount, updateRailState]);

  const move = (direction: -1 | 1) => {
    const rail = getRail();
    if (!rail) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    rail.scrollBy({
      behavior: reduceMotion ? "auto" : "smooth",
      left: direction * rail.clientWidth
    });
  };

  const indicatorStyle = {
    "--rail-progress": railState.progress
  } as CSSProperties;

  if (hideWhenNotScrollable && !railState.canScroll) return null;

  return (
    <>
      {showButtons ? (
        <div
          className={controlsClassName}
          data-node-id={controlsNodeId}
          data-testid={`${testIdPrefix}-controls`}
        >
          <button
            aria-controls={targetId}
            aria-label="Previous gallery images"
            data-node-id={previousNodeId}
            data-testid={`${testIdPrefix}-previous`}
            disabled={!railState.canGoPrevious}
            onClick={() => move(-1)}
            type="button"
          >
            <img alt="" aria-hidden="true" src="/profile-new/arrow.svg" />
          </button>
          <button
            aria-controls={targetId}
            aria-label="Next gallery images"
            data-node-id={nextNodeId}
            data-testid={`${testIdPrefix}-next`}
            disabled={!railState.canGoNext}
            onClick={() => move(1)}
            type="button"
          >
            <img alt="" aria-hidden="true" src="/profile-new/arrow.svg" />
          </button>
        </div>
      ) : null}

      {showIndicator ? (
        <div
          aria-hidden="true"
          className={indicatorClassName}
          data-node-id={indicatorNodeId}
          data-testid={`${testIdPrefix}-indicator`}
        >
          <span
            className={indicatorThumbClassName}
            data-node-id={indicatorThumbNodeId}
            style={indicatorStyle}
          />
        </div>
      ) : null}
    </>
  );
}
